import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shadow handoff redemption page.
 *
 * The PO tab opened a new tab pointing here with `?handoff=<single-use-id>`.
 * We POST to /api/platform-owner/shadow/redeem-handoff (no auth header — the
 * handoff itself is the proof). On success, we install the returned shadow
 * JWT into THIS tab's session (sessionStorage + the `is_shadow_tab` flag) so
 * the per-tab Storage.prototype patch in `public/index.html` keeps the rest
 * of the app reading the shadow token, while the original PO tab's
 * localStorage stays untouched. We then navigate to /dashboard via a hard
 * `window.location.replace` to guarantee a fresh AuthContext init.
 *
 * If redemption fails (expired / already used / target gone / admin lost
 * PO role), we render a clear error message with a back-to-login link —
 * we never silently fall through to a half-broken dashboard.
 */
export default function ShadowEnter() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const handoffId = searchParams.get('handoff');
  const [error, setError] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    // React 18 StrictMode double-invokes effects in dev. Guard so we never
    // attempt to redeem a single-use handoff twice from the same render.
    if (ranRef.current) return;
    ranRef.current = true;

    if (!handoffId) {
      setError('Missing handoff identifier in URL.');
      return;
    }

    (async () => {
      try {
        const resp = await axios.post(
          `${API_URL}/api/platform-owner/shadow/redeem-handoff`,
          { handoff_id: handoffId }
        );
        const { shadow_token, shadow_session } = resp.data || {};
        if (!shadow_token) {
          setError('Server response missing shadow token.');
          return;
        }

        // Install the shadow token in THIS tab's per-tab session.
        // sessionStorage is naturally tab-isolated, so the original PO tab
        // (and any other tab on this origin) cannot see this token.
        try {
          window.sessionStorage.setItem('is_shadow_tab', '1');
          window.sessionStorage.setItem('token', shadow_token);
          window.sessionStorage.setItem('token_stored_at', String(Date.now()));
        } catch (_) {}

        // Hard reload into /dashboard so AuthContext re-initialises against
        // the new sessionStorage token. `replace` avoids leaving /shadow/enter
        // in browser history (which would let the user back-navigate to a
        // dead handoff URL).
        const dest = `/dashboard?_shadow=1&_t=${Date.now()}`;
        window.location.replace(dest);
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to redeem shadow handoff.';
        setError(detail);
      }
    })();
  }, [handoffId, navigate]);

  if (error) {
    return (
      <div
        data-testid="shadow-enter-error"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0b1020',
          color: '#fff',
          padding: '2rem',
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: '100%',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 12,
            padding: '2rem',
          }}
        >
          <h1 style={{ fontSize: 22, marginBottom: 12 }}>Shadow session unavailable</h1>
          <p style={{ opacity: 0.8, lineHeight: 1.5 }}>{error}</p>
          <p style={{ opacity: 0.5, fontSize: 13, marginTop: 16 }}>
            Shadow handoffs are single-use and expire after 60 seconds. Please
            return to the Platform Owner tab and click "View as User" again.
          </p>
          <button
            onClick={() => {
              try { window.close(); } catch (_) {}
              if (!window.closed) window.location.replace('/login');
            }}
            data-testid="shadow-enter-close-btn"
            style={{
              marginTop: 20,
              background: '#7c3aed',
              color: '#fff',
              border: 'none',
              padding: '10px 18px',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Close tab
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="shadow-enter-loading"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0b1020',
        color: '#fff',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 36,
            height: 36,
            border: '3px solid rgba(255,255,255,0.2)',
            borderTopColor: '#7c3aed',
            borderRadius: '50%',
            animation: 'shadowSpin 0.9s linear infinite',
            margin: '0 auto 16px',
          }}
        />
        <div style={{ opacity: 0.75 }}>Establishing shadow session…</div>
      </div>
      <style>{`@keyframes shadowSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
